import type { Modification, TransactionRule } from '@carboncopyco/types';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useRef } from 'react';
import { type SpreadsheetAPI, TableToolsProvider } from '../JsonForm';
import {
  AddRuleButton,
  RulesContent,
  RulesManager,
  type RulesManagerProps,
} from '../RulesManager';
import { TransactionsContent } from './TransactionsContent';

export interface TransactionsManagerProps
  extends Pick<RulesManagerProps, 'isOpen' | 'onClose' | 'onLearnMoreClick'> {
  transactionModifications: Modification<'transaction_rule'>[];
  createTransactionRule: (
    transactionrule: TransactionRule,
    refresh?: boolean
  ) => Promise<unknown>;
  editTransactionModification: (
    transactionModification: Modification<'transaction_rule'>,
    refresh?: boolean
  ) => Promise<unknown>;
  deleteTransactionModification: (
    transactionModification: Modification<'transaction_rule'>
  ) => Promise<unknown>;
  reorderTransactionModification: (
    moved: Modification<'transaction_rule'>,
    after?: Modification<'transaction_rule'>
  ) => Promise<unknown>;
  spreadsheetApi: SpreadsheetAPI;
  openCreationModal: () => void;
}

const _helpLink =
  'https://www.notion.so/demostack/Data-Rules-How-To-8798490f2d4a4b47a83df58750674213';

export const TransactionsManager = ({
  isOpen,
  onClose,
  transactionModifications,
  spreadsheetApi,
  createTransactionRule,
  editTransactionModification,
  deleteTransactionModification,
  reorderTransactionModification,
  openCreationModal,
  onLearnMoreClick,
}: TransactionsManagerProps) => {
  const { editorRulesAndVarsLimit, editorDataRulesAdmin } = useFlags();
  const contentRef = useRef<HTMLDivElement>();

  const reachedLimit =
    transactionModifications.length >= editorRulesAndVarsLimit;

  return (
    <TableToolsProvider spreadsheetApi={spreadsheetApi}>
      <RulesManager
        isOpen={isOpen}
        onClose={onClose}
        onLearnMoreClick={onLearnMoreClick}
      >
        <RulesManager.Header>
          <RulesManager.Header.Title label="Data Rules" />
          <RulesManager.Header.Action>
            {editorDataRulesAdmin && (
              <AddRuleButton
                onClick={() => {
                  openCreationModal();
                  contentRef.current?.scrollTo({ top: 0 });
                }}
                disabled={reachedLimit}
                tooltipMessage={
                  reachedLimit &&
                  `You reached the limit of ${editorRulesAndVarsLimit} data rules`
                }
              />
            )}
          </RulesManager.Header.Action>
        </RulesManager.Header>
        {!!transactionModifications.length && (
          <div className="mt-2 mb-5">
            {/* <RulesManager.Subheader
            label="Modify network transactions."
            linkLabel="Learn how"
            linkUrl={helpLink}
          /> */}
          </div>
        )}
        {!transactionModifications.length ? (
          <RulesManager.EmptyState color="yellow">
            <RulesManager.EmptyState.Title label="Modify network transactions" />
            {/* <RulesManager.EmptyState.Subtitle
            label="Not sure how it works?"
            linkLabel="Learn more"
            linkUrl={helpLink}
          /> */}
          </RulesManager.EmptyState>
        ) : (
          <RulesContent>
            <RulesManager.Content ref={contentRef}>
              <TransactionsContent
                transactionModifications={transactionModifications}
                editTransactionModification={editTransactionModification}
                deleteTransactionModification={deleteTransactionModification}
                reorderTransactionModification={reorderTransactionModification}
                createTransactionRule={createTransactionRule}
              />
            </RulesManager.Content>
          </RulesContent>
        )}
      </RulesManager>
    </TableToolsProvider>
  );
};
